/**
 * AdminPullModal Component
 *
 * Admin modal for pulling CLAUDE.md from any agent (including other users' agents).
 * Allows saving as global template or to own templates.
 *
 * @module components/claude-templates/AdminPullModal
 */

import { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { Download, Loader2, X, Bot, Globe, User, AlertCircle, Eye, Save } from 'lucide-react';

const API_BASE = '/api';

/**
 * Fetch all agents (admin endpoint)
 */
async function fetchAllAgents() {
  const res = await fetch(`${API_BASE}/agents?status=active`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch agents');
  const data = await res.json();
  return data.data || [];
}

/**
 * Pull CLAUDE.md from agent (admin endpoint)
 */
async function adminPullFromAgent(agentId) {
  const res = await fetch(`${API_BASE}/admin/claude-templates/pull/${agentId}`, {
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to pull from agent');
  }
  return res.json();
}

/**
 * Save pulled content as template
 */
async function saveAsTemplate(data) {
  const res = await fetch(`${API_BASE}/claude-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to save template');
  }
  return res.json();
}

/**
 * AdminPullModal Component
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {function} props.onClose - Close callback
 * @param {function} props.onComplete - Callback when template is saved
 */
const AdminPullModal = memo(function AdminPullModal({
  isOpen,
  onClose,
  onComplete
}) {
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [pulledContent, setPulledContent] = useState(null);
  const [isPulling, setIsPulling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [saveAsGlobal, setSaveAsGlobal] = useState(false);

  // Group agents by owner
  const groupedAgents = useMemo(() => {
    const groups = {};
    agents.forEach(agent => {
      const ownerName = agent.owner?.displayName || agent.owner?.email || 'Unknown';
      if (!groups[ownerName]) {
        groups[ownerName] = [];
      }
      groups[ownerName].push(agent);
    });
    return groups;
  }, [agents]);

  // Load agents when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAgent(null);
      setPulledContent(null);
      setError(null);
      setTemplateName('');
      setSaveAsGlobal(false);
      loadAgents();
    }
  }, [isOpen]);

  const loadAgents = async () => {
    setIsLoadingAgents(true);
    try {
      const data = await fetchAllAgents();
      setAgents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const handlePull = useCallback(async () => {
    if (!selectedAgent) return;

    setIsPulling(true);
    setError(null);
    setPulledContent(null);

    try {
      const result = await adminPullFromAgent(selectedAgent.id);
      setPulledContent(result.content || result.data?.content);
      setTemplateName(`${selectedAgent.name}-template`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsPulling(false);
    }
  }, [selectedAgent]);

  const handleSave = useCallback(async () => {
    if (!pulledContent || !templateName.trim()) return;

    setIsSaving(true);
    setError(null);

    try {
      await saveAsTemplate({
        name: templateName.trim().toLowerCase().replace(/\s+/g, '-'),
        type: 'agent',
        content: pulledContent,
        isGlobal: saveAsGlobal
      });

      if (onComplete) {
        onComplete(templateName);
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [pulledContent, templateName, saveAsGlobal, onComplete, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Pull from Agent (Admin)</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPulling || isSaving}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoadingAgents ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : error && !pulledContent ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400 opacity-50" />
              <p className="text-red-400">{error}</p>
            </div>
          ) : pulledContent ? (
            /* Preview pulled content */
            <div className="p-4 space-y-4">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-300">Preview</span>
                </div>
                <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {pulledContent.slice(0, 2000)}
                  {pulledContent.length > 2000 && '...'}
                </pre>
              </div>

              {/* Save options */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Template name
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="my-template"
                    disabled={isSaving}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  />
                </div>

                <label className="flex items-center gap-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg cursor-pointer hover:border-gray-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={saveAsGlobal}
                    onChange={(e) => setSaveAsGlobal(e.target.checked)}
                    disabled={isSaving}
                    className="w-4 h-4 rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-900"
                  />
                  <Globe className="w-5 h-5 text-blue-400" />
                  <div>
                    <div className="font-medium text-gray-200">Save as Global Template</div>
                    <div className="text-xs text-gray-500">Visible to all users (read-only for non-admins)</div>
                  </div>
                </label>

                {!saveAsGlobal && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-800/30 rounded-lg">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-500">Will be saved to "My Templates"</span>
                  </div>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
            </div>
          ) : (
            /* Agent selection */
            <div className="p-4">
              <p className="text-sm text-gray-400 mb-4">
                Select an agent to pull CLAUDE.md from:
              </p>

              {Object.entries(groupedAgents).map(([ownerName, ownerAgents]) => (
                <div key={ownerName} className="mb-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                    {ownerName}
                  </div>
                  <div className="space-y-2">
                    {ownerAgents.map((agent) => (
                      <button
                        key={agent.id}
                        type="button"
                        onClick={() => setSelectedAgent(agent)}
                        className={`
                          w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors
                          ${selectedAgent?.id === agent.id
                            ? 'bg-indigo-600/20 border-indigo-500/50'
                            : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
                          }
                        `}
                      >
                        <Bot className="w-5 h-5 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-200 truncate">
                            {agent.displayName || agent.name}
                          </div>
                          {agent.host?.hostname && (
                            <div className="text-xs text-gray-500 truncate">
                              {agent.host.hostname}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {agents.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No agents available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-500">
            {pulledContent
              ? `Pulled from ${selectedAgent?.displayName || selectedAgent?.name}`
              : selectedAgent
                ? `Selected: ${selectedAgent.displayName || selectedAgent.name}`
                : 'Select an agent'
            }
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={pulledContent ? () => setPulledContent(null) : onClose}
              disabled={isPulling || isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {pulledContent ? 'Back' : 'Cancel'}
            </button>
            {pulledContent ? (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !templateName.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Template
              </button>
            ) : (
              <button
                type="button"
                onClick={handlePull}
                disabled={isPulling || !selectedAgent}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {isPulling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Pull CLAUDE.md
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default AdminPullModal;
